import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import {
  debounce as _debounce,
  find as _find,
  get as _get,
} from 'lodash';

import {
  addToCart,
  updateSelectedVariant,
  showModal,
} from 'actions';

import { constructPagePath } from 'utils/localeRouteGuide';
import { locale } from 'utils/locale';

import { addedProductPDP as trackAddedProductPDP} from 'analytics/cart';
import { showCourierModal as trackShowCourierModal} from 'analytics/courier';
import { showDeliveryMessage as trackShowDeliveryMessage} from 'analytics/messaging';
import { viewedProductPDP as trackViewedProductPDP} from 'analytics/product';
import { viewedProduct as lpViewedProduct } from 'analytics/livePerson';
import { hoverDebounce } from 'analytics/helpers';

import AffirmPriceLink from 'components/AffirmPriceLink/AffirmPriceLink';
import DividerWithText from 'components/DividerWithText/DividerWithText';
import { Icon } from 'sheets';
import FinancingModal from 'components/FinancingModal/FinancingModal';
import FinancingPriceLink from 'components/FinancingPriceLink/FinancingPriceLink';
import localize from 'components/Localize/Localize';
import MattressGallery from 'components/MattressGallery/MattressGallery';
import PayBrightLink from 'components/PayBrightLink/PayBrightLink';
import Popover from 'components/Popover/Popover';
import PriceGroup from 'components/PriceGroup/PriceGroup';
import ProductAvailability from 'components/ProductAvailability/ProductAvailability';
import ProductHeader from 'components/ProductHeader/ProductHeader';
import SpinningLoader from 'components/SpinningLoader/SpinningLoader';
import StructuredProductData from 'components/StructuredData/StructuredProductData/StructuredProductData';

import styles from './MattressVariantPurchaser.css';
import translations from './translations';

class MattressVariantPurchaser extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isAdding: false,
    };
    this.handleCourierModalClick = this.handleCourierModalClick.bind(this);
    this.getVariantSlug = this.getVariantSlug.bind(this);
  }

  componentWillMount() {
    this.variantSlug = this.getVariantSlug();

    this.variantInfo = _find(this.props.variants, (variant) => {
      return variant.attributes.options[`size-slug`] === this.variantSlug;
    });
  }

  componentWillReceiveProps(newProps) {
    if (this.state.isAdding && !newProps.isAddingToCart) {
      this.setState({ isAdding: false });
    }
  }

  componentDidMount() {
    trackViewedProductPDP({
      category: this.props.analytics.category,
      quantity: this.props.analytics.quantity,
      variantData: _find(this.props.variants, {id: this.props.analytics.id}),
    }, this.props.locale);

    lpViewedProduct({
      category: this.props.analytics.category,
      variant: _find(this.props.variants, {id: this.props.analytics.id}),
      locale: this.props.locale,
    });
  }

  getVariantSlug() {
    if (this.props.variantSlug) {
      return this.props.variantSlug;
    }
    const {location} = this.context.router.route;
    const pageSlug = location.pathname.split(`/`).filter((string) => string).pop();

    return locale(this.props.locale).isDACH() ? pageSlug.toUpperCase() : pageSlug;
  }

  getAvailabilityProps() {
    return {
      date: this.variantInfo.attributes[`ships-by`],
      isReadyToShip: this.variantInfo.attributes[`ready-to-ship`],
    };
  }

  getPriceGroupProps() {
    return {
      price: this.variantInfo.attributes[`price`],
    };
  }

  hasFinancingEU() {
    return locale(this.props.locale).isFR();
  }

  showFinancingLink() {
    if (locale(this.props.locale).isUS()) {
      return (
        <span className={styles[`affirm-link`]}>
          <AffirmPriceLink
            productType="mattress"
            {...this.getPriceGroupProps()}
          />
        </span>
      );
    }

    if (this.hasFinancingEU()) {
      return <FinancingPriceLink />;
    }

    if (locale(this.props.locale).isEnCA()) {
      return (
        <PayBrightLink
          {...this.getPriceGroupProps()}
        />
      );
    }
  }

  renderFinancingModal() {
    if (!this.hasFinancingEU()) {
      return null;
    }

    return <FinancingModal />;
  }

  handleAddToCart() {
    if (this.state.isAdding) {
      return;
    }
    this.setState({
      isAdding: true,
    }, this.props.addToCart.bind(this, this.variantInfo.id, this.props.locale));

    trackAddedProductPDP({
      variantData: _find(this.props.variants, {id: this.props.analytics.id}),
      category: this.props.analytics.category,
    }, this.props.locale);
  }

  renderShipsByTrigger() {
    return (
      <span>
        <ProductAvailability {...this.getAvailabilityProps()} />
        <span className={styles[`popover-circle`]}>?</span>
      </span>
    );
  }

  renderShipsByContent() {
    this.shipsByContent = this.getAvailabilityProps().isReadyToShip ? this.props.t.shipsByTooltip.available : this.props.t.shipsByTooltip.unavailable;

    return (
      <div>
        {this.shipsByContent}
      </div>
    );
  }

  handleCourierModalClick() {
    trackShowCourierModal(this.props.analytics.category);
    this.props.showModal(`courierEligible`);
  }

  renderCourierCalloutModalLink() {
    return (
      <span className={styles[`shipsby`]}>
        <button
          className={styles[`courier-callout-link`]}
          onClick={this.handleCourierModalClick}
        >
          {this.props.t.calloutMsg}
          <span className={styles[`icon-container`]}>
            <Icon
              className={styles[`arrow-icon`]}
              name="arrow"
            />
          </span>
        </button>
      </span>
    );
  }

  renderShipsByBlock() {
    const showCourier = this.getAvailabilityProps().isReadyToShip && this.props.withinCourier && locale(this.props.locale).isUS();

    if (showCourier) {
      return this.renderCourierCalloutModalLink();
    }

    const debouncedDeliveryPopoverAnalytics = _debounce(trackShowDeliveryMessage.bind(this, this.props.analytics.category, this.shipsByContent), hoverDebounce);

    const shipsByClass = classNames(
      styles[`fade-container`],
      styles[`shipsby`], {
        [styles[`fade-container--fade-in`]]: this.props.fetchingVariantsSuccess,
      }
    );

    return (
      <span className={shipsByClass}>
        <Popover
          content={this.renderShipsByContent()}
          showCallback={debouncedDeliveryPopoverAnalytics}
          trigger={this.renderShipsByTrigger()}
        />
      </span>
    );
  }

  renderSizeGuide() {
    if (locale(this.props.locale).isFR() || locale(this.props.locale).isGB()) {
      return null;
    }

    return (
      <div className={styles[`size-guide-container`]}>
        <a
          className={styles[`size-guide-link`]}
          href={this.props.t.sizeGuide.url}
        >
          {this.props.t.sizeGuide.text}
        </a>
      </div>
    );
  }

  getPageKey(variantKey) {
    const currentLocale = locale(this.props.locale);

    if (currentLocale.isGerman()) {
      return `matratzen-${variantKey}`;
    }

    return `mattress-${variantKey}`;
  }

  renderVariants() {
    return this.props.variants.map((variant, key) => {
      if (this.variantSlug === variant.attributes.options[`size-slug`]) {
        return null;
      }

      let variantSize = variant.attributes.options.size;
      if (locale(this.props.locale).isDACH()) {
        variantSize = variant.attributes.options[`size-slug`];
      }

      const pagePath = constructPagePath(this.props.locale, this.getPageKey(variant.attributes.options[`size-slug`].toLowerCase()));

      return (
        <a
          className={styles[`variant-link`]}
          href={pagePath}
          key={key}
        >
          {variantSize}
        </a>
      );
    });
  }

  renderDimensions() {
    if (locale(this.props.locale).isDACH()) {
      return null;
    }

    return (
      <span>{this.variantInfo.attributes.dimensions}</span>
    );
  }

  render() {
    return (
      <section className={styles[`wrapper`]}>
        <StructuredProductData customVariantData={this.props.t.markupData[this.variantSlug]} />
        {this.renderFinancingModal()}
        <MattressGallery product="mattress" />
        <div className={styles[`container`]}>
          <div className={styles[`container-inner`]}>
            <ProductHeader
              descriptionStyles={styles[`product-header-description`]}
              productType={this.variantSlug}
            />
            <div className={styles[`purchaser`]}>
              <div className={styles[`purchaser-meta`]}>
                {this.renderDimensions()}
                {this.renderSizeGuide()}
                {this.renderShipsByBlock()}
              </div>
              <div className={styles[`purchasing-cont`]}>
                <div className={styles[`purchasing`]}>
                  <PriceGroup
                    taxIncluded={this.props.t.taxIncluded}
                    {...this.getPriceGroupProps()}
                  />
                  <button
                    className={styles[`add-to-cart`]}
                    onClick={this.handleAddToCart.bind(this)}
                    type="button"
                  >
                    {this.props.updatingCart ? <SpinningLoader /> : this.props.t.addToCartCta}
                  </button>
                </div>
                {this.showFinancingLink()}
              </div>
            </div>
            <div className={styles[`divider-cont`]}>
              <DividerWithText />
            </div>
            <div className={styles[`variants-cont`]}>
              {this.renderVariants()}
            </div>
          </div>
        </div>
      </section>
    );
  }
}

MattressVariantPurchaser.propTypes = {
  analytics: PropTypes.shape({
    cart: PropTypes.object.isRequired,
    category: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
  }).isRequired,
  casperReviewsTotalCount: PropTypes.number,
  fetchingVariantsSuccess: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  t: PropTypes.shape({
    addToCartCta: PropTypes.string.isRequired,
    calloutMsg: PropTypes.string,
    markupData: PropTypes.object,
    shipsByTooltip: PropTypes.object,
    sizeGuide: PropTypes.object,
    taxIncluded: PropTypes.string,
    variantList: PropTypes.array,
  }),
  variantSlug: PropTypes.string,
  variants: PropTypes.array.isRequired,
  withinCourier: PropTypes.bool,
};

MattressVariantPurchaser.contextTypes = {
  router: PropTypes.object,
};

const mapStateToProps = (state) => {
  return {
    analytics: {
      cart: _get(state, `cart`),
      category: _get(state, `product.product.category`),
      id: _get(state, `selectedVariants.mattress.id`),
      quantity: _get(state, `selectedVariants.mattress.quantity`),
    },
    fetchingVariantsSuccess: _get(state, `product.fetchingVariantsSuccess`),
    isAddingToCart: _get(state, `cart.adding`),
    updatingCart: _get(state, `cart.updating`),
    variants: _get(state, `product.variants`),
    withinCourier: _get(state, `courier.withinCourier`),
  };
};

const mapDispatchToProps = (dispatch) => ({
  addToCart: (selectedVariantID) => {
    const items = [{
      variant_id: parseInt(selectedVariantID, 10), // eslint-disable-line camelcase
    }];
    dispatch(addToCart(items));
  },

  showModal: (key) => {
    dispatch(showModal(key));
  },

  updateSelectedMattressVariant: (param) => {
    dispatch(updateSelectedVariant(`mattress`, param));
  },
});

export default localize(connect(mapStateToProps, mapDispatchToProps)(MattressVariantPurchaser), translations);
